import {
  formatParisDateTime,
  RECHARGE_FEATURE,
  RECHARGE_FEATURE_MODE,
  RECHARGE_WEBHOOK_OUTCOME,
  type RechargeWebhookEventDetail,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchWebhookEvent } from './api.js';
import { DetailSheet } from './detail-sheet.js';
import { WebhookFeature, WebhookObject } from './webhook-object.js';

const DETAIL_ROWS = 8;

function Dash(): JSX.Element {
  return <span className="text-muted-foreground">—</span>;
}

function DateValue({ value }: { value: string | null }): JSX.Element {
  return value === null ? (
    <Dash />
  ) : (
    <span className="whitespace-nowrap tabular-nums">{formatParisDateTime(new Date(value))}</span>
  );
}

function WebhookDetailSkeleton(): JSX.Element {
  return (
    <div aria-busy="true" aria-label="Chargement du webhook" className="flex flex-col gap-3">
      {Array.from({ length: DETAIL_ROWS }, (_, index) => (
        <div
          key={`detail-skeleton-${String(index)}`}
          className="grid grid-cols-1 gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-6"
        >
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-3.5 w-full max-w-56" />
        </div>
      ))}
      <Skeleton className="mt-2 h-9 w-full" />
    </div>
  );
}

function WebhookDetailBody({ event }: { event: RechargeWebhookEventDetail }): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <DescriptionList>
        <DescriptionItem label="Reçu le">
          <DateValue value={event.receivedAt} />
        </DescriptionItem>
        <DescriptionItem label="Traité le">
          <DateValue value={event.processedAt} />
        </DescriptionItem>
        <DescriptionItem label="Topic">
          <span className="font-mono text-xs" translate="no">
            {event.topic}
          </span>
        </DescriptionItem>
        <DescriptionItem label="Objet">
          <WebhookObject objectType={event.objectType} objectId={event.objectId} />
        </DescriptionItem>
        <DescriptionItem label="Fonction">
          <WebhookFeature feature={event.feature} />
        </DescriptionItem>
        <DescriptionItem label="Mode">
          {event.mode === null ? (
            <Dash />
          ) : (
            <StatusBadge registry={RECHARGE_FEATURE_MODE} code={event.mode} />
          )}
        </DescriptionItem>
        <DescriptionItem label="Résultat">
          <StatusBadge registry={RECHARGE_WEBHOOK_OUTCOME} code={event.outcome} />
        </DescriptionItem>
        <DescriptionItem label="Raison">{event.reason ?? <Dash />}</DescriptionItem>
        {event.ref === null ? null : (
          <DescriptionItem label="Élément créé">
            {RECHARGE_FEATURE[event.ref.feature].label}{' '}
            <span className="font-mono text-xs" translate="no">
              {event.ref.id}
            </span>
          </DescriptionItem>
        )}
      </DescriptionList>

      <details className="group border-t border-border pt-4">
        <summary className="cursor-pointer select-none rounded-sm text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          Payload reçu
        </summary>
        <pre
          translate="no"
          className="mt-3 max-h-[28rem] overflow-auto overscroll-contain whitespace-pre rounded-md border border-border bg-muted p-3 font-mono text-xs text-foreground"
        >
          {JSON.stringify(event.payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/**
 * Panneau de détail d'un webhook Recharge (`06` §8.5), ouvert par `?id=`.
 * Le contenu vient de `GET /api/recharge/webhook-events/:id` : la liste ne
 * transporte pas le payload.
 */
export function WebhookDetailSheet({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.subscriptions.webhookEvent(id ?? ''),
    queryFn: () => fetchWebhookEvent(id ?? ''),
    enabled: id !== null,
  });

  const event = query.data;
  const error = query.error instanceof ApiError ? query.error : null;
  const isNotFound = error?.status === 404;

  return (
    <DetailSheet
      open={id !== null}
      onClose={onClose}
      title={
        event ? (
          <span className="font-mono" translate="no">
            {event.topic}
          </span>
        ) : (
          'Webhook'
        )
      }
      {...(event ? { description: `Reçu le ${formatParisDateTime(new Date(event.receivedAt))}` } : {})}
    >
      {query.isPending ? (
        <WebhookDetailSkeleton />
      ) : query.isError ? (
        <ErrorState
          message={error?.message ?? 'Impossible de charger ce webhook.'}
          detail={error?.detail}
          {...(isNotFound
            ? {}
            : {
                onRetry: () => {
                  void query.refetch();
                },
              })}
        />
      ) : (
        <WebhookDetailBody event={query.data} />
      )}
    </DetailSheet>
  );
}
